import { normalizeSharedConsentSnapshot } from "./shared-consent";
import {
  isActiveTestEnrollment,
  TEST_ENROLLMENT_SOURCE,
} from "./test-enrollment";
/**
 * Consent resolution helpers.
 * Order of precedence:
 * stored state -> config defaults -> GPC/DNT -> Shopify -> TCF.
 *
 * Additional signals (recorded as metadata only):
 * - US Privacy (`__uspapi`)
 * - IAB GPP (`__gpp`)
 */
import type {
  ConsentPurposeEvidence,
  ConsentSnapshot,
  ConsentState,
  PixelConfig,
} from "./types";
import { safeNavigator } from "./utils";

export type StoredConsent = {
  state: ConsentState;
  updatedAt: number;
  cmp?: ConsentSnapshot["cmp"];
};

export function readStoredConsent(raw: string | null): StoredConsent | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const normalized = normalizeSharedConsentSnapshot(parsed);
    if (!normalized) return null;
    return {
      state: normalized.state,
      updatedAt:
        typeof parsed.updatedAt === "number" &&
        Number.isFinite(parsed.updatedAt)
          ? parsed.updatedAt
          : 0,
      cmp: normalized.cmp,
    };
  } catch {
    return null;
  }
}

function readDnt(): boolean {
  const nav = safeNavigator();
  const win = typeof window === "undefined" ? undefined : (window as any);
  const value = nav?.doNotTrack ?? win?.doNotTrack;
  return value === "1" || value === "yes";
}

function readGpc(): boolean {
  const nav = safeNavigator() as
    | (Navigator & { globalPrivacyControl?: boolean })
    | undefined;
  return Boolean(nav?.globalPrivacyControl);
}

function readShopifyBoolean(
  shopify: Record<string, unknown>,
  method: string,
): boolean | undefined {
  try {
    const reader = shopify[method];
    if (typeof reader !== "function") return undefined;
    const value = reader.call(shopify);
    return typeof value === "boolean" ? value : undefined;
  } catch {
    return undefined;
  }
}

function readShopifyConsent(): {
  state?: ConsentState;
  hasSignal?: boolean;
  purposes?: ConsentPurposeEvidence;
  raw?: unknown;
} {
  const win = typeof window === "undefined" ? undefined : (window as any);
  const shopify = win?.Shopify?.customerPrivacy as
    | Record<string, unknown>
    | undefined;
  if (!shopify) return {};

  const tracking = readShopifyBoolean(shopify, "getTrackingConsent");
  const analytics = readShopifyBoolean(shopify, "analyticsProcessingAllowed");
  const marketing = readShopifyBoolean(shopify, "marketingAllowed");
  const hasSignal = [tracking, analytics, marketing].some(
    (value) => value !== undefined,
  );
  if (!hasSignal) return {};

  const raw = { tracking, analytics, marketing };
  const purposes: ConsentPurposeEvidence = {
    version: 1,
    analytics: analytics === true,
    marketing: marketing === true,
  };
  if (tracking === false || analytics === false || marketing === false) {
    return { state: "denied", hasSignal, purposes, raw };
  }
  if (analytics === true && marketing === true) {
    return { state: "granted", hasSignal, purposes, raw };
  }
  return { state: "unknown", hasSignal, purposes, raw };
}

function readUspConsent(): Promise<{ uspString?: string; raw?: unknown }> {
  const win = typeof window === "undefined" ? undefined : (window as any);
  const uspapi = win?.__uspapi;
  if (!uspapi) return Promise.resolve({});

  return new Promise((resolve) => {
    try {
      uspapi("getUSPData", 1, (uspData: any, success: boolean) => {
        if (!success || !uspData) return resolve({});
        const uspString =
          typeof uspData.uspString === "string" ? uspData.uspString : undefined;
        return resolve({ uspString, raw: uspData });
      });
    } catch {
      resolve({});
    }
  });
}

function readGppConsent(): Promise<{
  gppString?: string;
  sectionIds?: number[];
  raw?: unknown;
}> {
  const win = typeof window === "undefined" ? undefined : (window as any);
  const gpp = win?.__gpp;
  if (!gpp) return Promise.resolve({});

  return new Promise((resolve) => {
    try {
      gpp("getGPPData", (gppData: any, success: boolean) => {
        if (!success || !gppData) return resolve({});
        const gppString =
          typeof gppData.gppString === "string" ? gppData.gppString : undefined;
        const sectionIds = Array.isArray(gppData.applicableSections)
          ? gppData.applicableSections
              .map((value: unknown) => Number(value))
              .filter((value: number) => Number.isFinite(value))
          : undefined;
        return resolve({
          gppString,
          sectionIds: sectionIds?.length ? sectionIds : undefined,
          raw: gppData,
        });
      });
    } catch {
      resolve({});
    }
  });
}

function readTcfConsent(): Promise<{
  state?: ConsentState;
  tcString?: string;
  purposes?: ConsentPurposeEvidence;
  raw?: unknown;
}> {
  const win = typeof window === "undefined" ? undefined : (window as any);
  const tcfapi = win?.__tcfapi;
  if (!tcfapi) return Promise.resolve({});

  return new Promise((resolve) => {
    try {
      tcfapi("getTCData", 2, (tcData: any, success: boolean) => {
        if (!success || !tcData) return resolve({});
        const requiredPurposes = [1, 3, 4] as const;
        const consents = tcData?.purpose?.consents;
        const decisions = requiredPurposes.map((purpose) =>
          typeof consents?.[purpose] === "boolean"
            ? (consents[purpose] as boolean)
            : undefined,
        );
        const tcfPurposes = {
          "1": decisions[0] === true,
          "3": decisions[1] === true,
          "4": decisions[2] === true,
        };
        const purposes: ConsentPurposeEvidence = {
          version: 1,
          analytics: tcfPurposes["1"],
          marketing: tcfPurposes["1"] && tcfPurposes["3"] && tcfPurposes["4"],
          tcf: tcfPurposes,
        };
        if (tcData.gdprApplies === false) {
          return resolve({
            state: "granted",
            tcString: tcData.tcString,
            purposes,
            raw: tcData,
          });
        }
        // ProsPix currently has one state that would authorize both analytics
        // and marketing. TCF purpose 1 alone covers storage/access and cannot
        // be promoted into that broader grant. Require the bounded purposes
        // used by the current marketing contract; incomplete data stays
        // unknown, while any explicit refusal fails closed.
        const state: ConsentState = decisions.some(
          (decision) => decision === false,
        )
          ? "denied"
          : decisions.every((decision) => decision === true)
            ? "granted"
            : "unknown";
        return resolve({
          state,
          tcString: tcData.tcString,
          purposes,
          raw: tcData,
        });
      });
    } catch {
      resolve({});
    }
  });
}

export async function resolveConsent(
  config: PixelConfig,
  stored: StoredConsent | null,
): Promise<ConsentSnapshot> {
  // A temporary operator policy is not a saved visitor decision. Removing the
  // remote switch must restore the chooser instead of retaining this grant.
  if (stored?.cmp?.source === "prospix_operator_preview_v1") stored = null;
  let state: ConsentState = stored?.state ?? config.privacy.defaultConsent;
  const gpc = readGpc();
  const dnt = readDnt();

  if (
    stored?.cmp?.source === TEST_ENROLLMENT_SOURCE ||
    stored?.cmp?.source === "prospix_test_expired_v1" ||
    stored?.cmp?.source === "prospix_test_withdrawal_v1"
  ) {
    if (
      !isActiveTestEnrollment({ ...stored, gpc, dnt }, Date.now()) ||
      gpc ||
      dnt
    )
      return {
        state: "denied",
        gpc,
        dnt,
        cmp: { source: "prospix_test_expired_v1" },
      };
    const shopify = readShopifyConsent();
    const tcf = await readTcfConsent();
    if (shopify.state === "denied" || tcf.state === "denied")
      return {
        state: "denied",
        gpc,
        dnt,
        cmp: { source: "prospix_test_withdrawal_v1" },
      };
    return { ...stored, gpc, dnt };
  }
  if (config.privacy.consentEnforcement === "preview") {
    return {
      state: gpc || dnt ? "denied" : "granted",
      gpc,
      dnt,
      cmp: {
        source: "prospix_operator_preview_v1",
        purposes: {
          version: 1,
          analytics: !gpc && !dnt,
          marketing: !gpc && !dnt,
        },
      },
    };
  }

  const gpcIsAbsolute = gpc;
  const dntIsAbsolute = dnt;
  // Trusted browser privacy signals take precedence over stored or later CMP
  // grants for this release.
  if (gpcIsAbsolute) {
    state = "denied";
  } else if (dntIsAbsolute) {
    state = "denied";
  }

  const shopify = readShopifyConsent();
  const [tcf, usp, gpp] = await Promise.all([
    readTcfConsent(),
    readUspConsent(),
    readGppConsent(),
  ]);
  if (!gpcIsAbsolute && !dntIsAbsolute) {
    if (shopify.state === "denied" || tcf.state === "denied") {
      // A current authoritative denial dominates a stored grant or another
      // simultaneous affirmative CMP signal.
      state = "denied";
    } else if (shopify.state === "unknown" || tcf.state === "unknown") {
      // Shopify and TCF expose separate purposes. If either reports only part
      // of the decision, a cached grant must not fill in the missing purpose
      // or authorize marketing side effects.
      state = "unknown";
    } else if (shopify.state === "granted" || tcf.state === "granted") {
      // Current host CMP state is newer than the cached local decision. This
      // also allows a legitimate later regrant after a prior denial.
      state = "granted";
    }
  }

  const includeRaw = Boolean(config.debug);
  const authoritativeLiveSource =
    tcf.state === "denied"
      ? "tcf"
      : shopify.state === "denied"
        ? "shopify"
        : tcf.state === "unknown"
          ? "tcf"
          : shopify.state === "unknown"
            ? "shopify"
            : tcf.state === "granted"
              ? "tcf"
              : shopify.state === "granted"
                ? "shopify"
                : undefined;
  const retainedStoredCmp = authoritativeLiveSource ? undefined : stored?.cmp;
  const metadataOnlySource = gpp.gppString
    ? "gpp"
    : usp.uspString
      ? "usp"
      : gpp.sectionIds?.length
        ? "gpp"
        : undefined;
  const cmpSource =
    authoritativeLiveSource ?? retainedStoredCmp?.source ?? metadataOnlySource;
  const hasCmpSignals = Boolean(
    cmpSource ||
    retainedStoredCmp ||
    tcf.tcString ||
    usp.uspString ||
    gpp.gppString ||
    gpp.sectionIds?.length,
  );
  const cmp = hasCmpSignals
    ? {
        source: cmpSource ?? "unknown",
        tcString: tcf.tcString ?? retainedStoredCmp?.tcString,
        uspString: usp.uspString ?? retainedStoredCmp?.uspString,
        gppString: gpp.gppString ?? retainedStoredCmp?.gppString,
        gppSectionIds: gpp.sectionIds ?? retainedStoredCmp?.gppSectionIds,
        purposes:
          authoritativeLiveSource === "tcf"
            ? tcf.purposes
            : authoritativeLiveSource === "shopify"
              ? shopify.purposes
              : (retainedStoredCmp?.purposes ??
                (metadataOnlySource === "usp" && usp.uspString
                  ? {
                      version: 1 as const,
                      analytics: true,
                      marketing:
                        /^1[YN][YN][YN]$/i.test(usp.uspString) &&
                        usp.uspString[2]?.toUpperCase() === "N",
                    }
                  : undefined)),
        ...(includeRaw
          ? {
              raw: {
                ...(tcf.raw ? { tcf: tcf.raw } : {}),
                ...(shopify.raw ? { shopify: shopify.raw } : {}),
                ...(usp.raw ? { usp: usp.raw } : {}),
                ...(gpp.raw ? { gpp: gpp.raw } : {}),
              },
            }
          : {}),
      }
    : undefined;

  return { state, gpc, dnt, cmp };
}
